import { LitElement, html, css } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { CoordinatesUtils } from '../../../src/utils/coordinates-utils';
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style';

@customElement('voy-lat-long-input')
export class VoyLatLongInput extends LitElement
{
    @property({type: Boolean}) long: boolean = false; // Longitude input (vs latitude input)
    @property({type: Boolean, reflect: true}) disabled: boolean = false;
    @property({type: Boolean, reflect: true}) invalid: boolean = true;

    @property({type: Number})
    get value()
    {
        return this._value;
    }

    set value(newValue: number|null)
    {
        if (newValue != this._value)
        {
            const oldValue = this._value;
            this._value = newValue;
            this.requestUpdate("value", oldValue);
            if (!this._userTyping)
            {
                this._setInputFromValue();
            }
        }
    }

    static get styles()
    {
        return [
            voyageCursorStyles,
            css`
            :host {
                height: fit-content;
                width: 180px;
                padding-left: 10px;
                padding-right: 10px;
                border: 1px solid var(--neutrals-grey-06);
                background-color: var(--neutrals-grey-10, white);
                border-radius: 3px;
            }

            :host([invalid]) {
                border: 1px solid var(--voyage-error-red, red);
            }

            :host([disabled]) {
                background-color: var(--neutrals-grey-07, grey);
            }

            :host(:focus-within) {
                box-shadow: 0px 4px 12px rgba(var(--neutrals-grey-01-rgb), 0.06), 0px 2px 4px rgba(var(--neutrals-grey-01-rgb), 0.1);
            }

            input {
                font-family: MaerskText;
                font-size: 14px;
                box-sizing: border-box;
                outline: none;
                border: none;
                background: none;
                color: var(--neutrals-grey-02);
                height: 100%;
                width: 100%;
            }

            input:invalid {
                color: var(--voyage-error-red, red);
            }
            `
        ];
    }

    render()
    {
        return html`
        <input type="text" required .disabled=${this.disabled} .value=${this._inputValue} @input=${this._onInput} @blur=${this._onBlur}>
        `;
    }

    firstUpdated()
    {
        this._input = this.shadowRoot!.querySelector("input");
    }

    @property({attribute: false}) private _inputValue: string = "";
    private _value: number|null = null;
    private _userTyping: boolean = false;
    private _input: HTMLInputElement|null = null;

    private _setInputFromValue()
    {
        let inputValue = "";
        if (this._value !== null)
        {
            const coords = this.long ? [this._value, 0] : [0, this._value];
            const degCoord = CoordinatesUtils.decimalToDegreesAndMinutes(coords);
            inputValue = this.long ? degCoord.lon : degCoord.lat;
        }
        this._inputValue = inputValue;
        this.invalid = this._value === null;
        if (this._input)
        {
            this._input!.setCustomValidity("");
        }
    }

    private _onInput(): void
    {
        this._inputValue = this._input!.value;
        try
        {
            const decimalDeg = CoordinatesUtils.degreesToDecimal(this._input!.value);
            this._input!.setCustomValidity("");
            this._value = decimalDeg;
        }
        catch(err)
        {
            this._input!.setCustomValidity(err.message);
        }
        this.invalid = !this._input!.validity.valid;
        this._userTyping = true;
    }

    private _onBlur(): void
    {
        // Trigger "pretty-print" in case the user has changed something
        this._userTyping = false;
        this._setInputFromValue();
    }
};
